import React, {useEffect, useState, useCallback} from 'react'
import {makeStyles} from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import MenuIcon from '@material-ui/icons/Menu';
import IconButton from "@material-ui/core/IconButton";
import {NavLink} from "react-router-dom";
import * as theme from "../../theme";
import {SIDEBAR_WIDTH, SIDEBAR_COLLAPSED_WIDTH} from "./constants";

const useStyles = makeStyles({
    sidebar: {
        background: theme.gray10,
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        position: 'sticky',
        top: 0,
        borderRight: `1px solid ${theme.gray30}`,
        overflow: 'hidden',
        transition: 'width 0.3s ease, min-width 0.3s ease',
    },
    navScroll: {
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        '&::-webkit-scrollbar': {width: 4},
        '&::-webkit-scrollbar-track': {background: 'transparent'},
        '&::-webkit-scrollbar-thumb': {background: 'rgba(0,0,0,0.15)', borderRadius: 4},
        '&::-webkit-scrollbar-thumb:hover': {background: 'rgba(0,0,0,0.25)'},
        scrollbarWidth: 'thin',
        scrollbarColor: 'rgba(0,0,0,0.15) transparent',
    },
    logoHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: 56,
        borderBottom: `1px solid ${theme.gray30}`,
        flexShrink: 0,
    },
    collapseBtn: {
        position: 'absolute',
        right: -14,
        top: 24,
        width: 28,
        height: 28,
        backgroundColor: theme.white,
        border: `1px solid ${theme.gray30}`,
        boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
        zIndex: 1300,
        '&:hover': {backgroundColor: theme.gray05},
    },
    bottomSection: {
        marginTop: 'auto',
        borderTop: `1px solid ${theme.gray30}`,
        flexShrink: 0,
    },
    backdrop: {
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.4)',
        zIndex: 1199,
    },
    hamburger: {
        position: 'fixed',
        top: 8, left: 8,
        zIndex: 1100,
        backgroundColor: theme.white,
        border: `1px solid ${theme.gray30}`,
        boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
        '&:hover': {backgroundColor: theme.gray05},
    },
});

/**
 * Reusable sidebar layout shell.
 *
 * Handles responsive behavior, collapse/expand, logo, hamburger menu, and backdrop.
 * Accepts `renderNav` and `renderBottom` render props for menu content.
 *
 * Props:
 *   - isCollapsed / setIsCollapsed: controlled collapse state
 *   - logo: optional logo src (defaults to "/DRIVO_Logo.png")
 *   - collapsedLogo: optional collapsed logo (defaults to chevron SVG)
 *   - renderNav({ collapsed, iconSize, fontSize, onNavClick }): main nav content
 *   - renderBottom({ collapsed, iconSize, fontSize, onNavClick }): bottom section content
 *   - width / collapsedWidth: optional custom widths
 */
export default function SidebarLayout({
    isCollapsed,
    setIsCollapsed,
    logo = "/DRIVO_Logo.png",
    collapsedLogo,
    renderNav,
    renderBottom,
    width = SIDEBAR_WIDTH,
    collapsedWidth = SIDEBAR_COLLAPSED_WIDTH,
}) {
    const classes = useStyles();

    const isSmallScreen = useMediaQuery('(max-width:1024px)');
    const [drawerOpen, setDrawerOpen] = useState(false);

    useEffect(() => {
        if (isSmallScreen) { setIsCollapsed(true); setDrawerOpen(false); }
    }, [isSmallScreen]);

    const handleNavClick = useCallback(() => {
        if (isSmallScreen) setDrawerOpen(false);
    }, [isSmallScreen]);

    const collapsed = isSmallScreen ? false : isCollapsed;

    const isMobile = useMediaQuery('(max-width:480px)');
    const iconSize = isMobile ? 18 : 20;
    const fontSize = isMobile ? 13 : 15;

    const wrapperStyle = isSmallScreen
        ? {position: 'fixed', top: 0, left: 0, zIndex: 1200, width, height: '100vh', transform: drawerOpen ? 'translateX(0)' : 'translateX(-100%)', transition: 'transform 0.3s ease'}
        : {position: 'relative', flexShrink: 0, width: collapsed ? collapsedWidth : width, minWidth: collapsed ? collapsedWidth : width, transition: 'width 0.3s ease, min-width 0.3s ease'};

    const renderProps = {collapsed, iconSize, fontSize, onNavClick: handleNavClick};

    const defaultCollapsedLogo = (
        <svg height="28" width="28" viewBox="0 0 24 24" fill="none">
            <path d="M9 6L15 12L9 18" stroke="#84cc16" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
    );

    return (
        <>
            {isSmallScreen && !drawerOpen && (
                <IconButton className={classes.hamburger} size="small" onClick={() => setDrawerOpen(true)} aria-label="Open menu">
                    <MenuIcon />
                </IconButton>
            )}

            {isSmallScreen && drawerOpen && (
                <div className={classes.backdrop} onClick={() => setDrawerOpen(false)} />
            )}

            <div style={wrapperStyle}>
                <div className={classes.sidebar} style={{width: '100%'}}>
                    <div className={classes.logoHeader}>
                        {isSmallScreen ? (
                            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: '0 12px'}}>
                                <NavLink to="/" onClick={handleNavClick} style={{display: 'flex', alignItems: 'center'}}>
                                    <img src={logo} alt="Drivo" style={{height: 30}}/>
                                </NavLink>
                                <IconButton size="small" onClick={() => setDrawerOpen(false)} aria-label="Close menu">
                                    <ChevronLeftIcon fontSize="small"/>
                                </IconButton>
                            </div>
                        ) : collapsed ? (
                            <NavLink to="/" style={{display: 'flex', alignItems: 'center'}}>
                                {collapsedLogo || defaultCollapsedLogo}
                            </NavLink>
                        ) : (
                            <NavLink to="/" style={{display: 'flex', alignItems: 'center'}}>
                                <img src={logo} alt="Drivo" style={{height: 30}}/>
                            </NavLink>
                        )}
                    </div>

                    <nav className={classes.navScroll} style={{padding: '8px 4px'}}>
                        {renderNav && renderNav(renderProps)}
                    </nav>

                    {renderBottom && (
                        <div className={classes.bottomSection}>
                            {renderBottom(renderProps)}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}